import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Modal,
  Col,
  Typography,
  Form,
  Row,
  Layout,
  Input,
  Button,
  message,
  Spin,
  Space,
  Table,
  Tooltip,
  DatePicker,
  Select,
  InputNumber,
  Upload,
  notification,
  Image,
  Radio,
  Drawer,
  Dropdown,
} from 'antd';
import { Checkbox, Button as CrcButton, Spinner } from '@crc-evans/ui-components';
import {
  File as FileIcon,
  FileText,
  Image as LucideImage,
  Pencil,
  Copy,
  Plus,
  Share2,
  Trash2,
  Filter,
} from 'lucide-react';
import {
  MailOutlined,
  CloudUploadOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  SearchOutlined,
  PlusOutlined,
  HeatMapOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  CalendarOutlined,
  CloseOutlined,
  FileTextOutlined,
  FileImageOutlined,
  DownOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { connect } from 'react-redux';
import get from 'lodash/get';
import axiosConfig from '../redux/heatProfile/heatProfileAxiosConfig';
import omit from 'lodash/omit';
import EmailModal from './EmailModal';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { wrapText } from '../utils/pdfUtils';
import { generateCombinedPdfReport } from '../utils/combinedPdfReportUtils';

// Set worker path for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
import history from '../services/history';
import { fetchHeatProfileList } from '../redux/heatProfile/action';
import styled from 'styled-components';
import { NotifyEmails } from '../redux/heatProfile/saga';
import SignaturePad from './Heatprofile/Signature_Pad';
import { Content, Footer } from 'antd/lib/layout/layout';

const { Title, Text } = Typography;
const { Option } = Select;

const sanitizeFileName = (fileName) => {
  const parts = fileName.split('.');
  const extension = parts.pop(); // Take extension
  const nameWithoutExt = parts.join('_'); // Replace middle dots with underscores

  // Replace invalid characters (including quotes, slashes, etc.)
  const sanitized = nameWithoutExt.replace(/[<>:"/\\|?*']/g, '_');

  console.log('result', `${sanitized}.${extension}`);
  return `${sanitized}.${extension}`;
};

/**
 * Custom dropdown that matches the Heat Profile modal input UI (same height, border-radius, focus).
 * Use with Form.Item; accepts value + onChange for controlled usage.
 */
const CustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : { value: opt.value, label: opt.label ?? opt.value }
  );
  const selectedLabel = normalizedOptions.find((o) => o.value === value)?.label ?? value ?? '';

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className={`heat-profile-custom-select ${className} ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="heat-profile-custom-select-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
          if (e.key === 'Escape') setOpen(false);
        }}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
      >
        <span className="heat-profile-custom-select-value">
          {selectedLabel || <span className="placeholder">{placeholder}</span>}
        </span>
        <DownOutlined className="heat-profile-custom-select-arrow" />
      </div>
      {open && (
        <div className="heat-profile-custom-select-dropdown" role="listbox">
          {normalizedOptions.map((opt) => (
            <div
              key={opt.value}
              className={`heat-profile-custom-select-option ${opt.value === value ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onChange(opt.value);
                  setOpen(false);
                }
              }}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HeatProfile = (props) => {
  const { onProjectList, projectList, isLoading, listCount } = props;

  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user'));
  let name = '';
  if (user) {
    if (user.name) {
      name = user.name;
    } else {
      name = (user['custom:first_name'] || '') + ' ' + (user['custom:last_name'] || '');
    }
  }

  const validation_fields = {
    reportNumber: 'Report Number',
    projectName: 'Project Name',
    projectNumber: 'Project Number',
    trailDate: 'Trial Date',
    generatorId: 'Generator Id',
    inductionCoilId: 'Induction Coil ID',
    steelOD: 'Steel OD',
    wt: 'Wt',
    generatorSettings: 'Generator Settings',
    coilLength: 'CoilLength',
    cableLength: 'Cable length',
    numberOfTurns: 'Number of Turns',
    materialClass: 'Material Class',
    material: 'Material',
    cutbackLength: 'Cut back Length',
    parentCoatingThickness: 'Parent Coating Thickness',
    probeTemp: 'Probe Temp',
    probeTempLocation: 'Probe temp Location',
  };

  const initialFormState = {
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    reportAuthor: name,
    trailDate: '',
    generatorId: '',
    inductionCoilId: '',
    steelOD: '',
    wt: '',
    //
    generatorSettings: '',
    coilLength: '',
    cableLength: '',
    numberOfTurns: '',
    //
    materialClass: '',
    material: '',
    stages: 1,
    maxTemp: '0',
    minTemp: '0',
    maxTempS1: '0',
    minTempS1: '0',
    //
    cutbackLength: '',
    parentCoatingThickness: '',
    probeTemp: '',
    probeTempLocation: '',
    //
    signatureRequired: 0,

    deployEnv: process.env.REACT_APP_STAGE,
    imageFileDetails: {},
    required: {
      generatorSettings: 'Yes',
      coilLength: 'Yes',
      cableLength: 'Yes',
      numberOfTurns: 'Yes',
    },
    units: {
      steelOD: 'mm',
      wt: 'mm',
      coilLength: 'mm',
      cableLength: 'meters',
      parentCoatingThickness: 'mm',
    },
  };
  const temperatureUnit = '°C';
  const lengthUnit = 'mm';
  //const cableLengthUnit = 'm';
  //const wtUnit = 'mm';

  const Units = [{ value: 'mm' }, { value: 'inch' }];
  const materialClassOptions = [
    'FBE',
    'Liquid Epoxy',
    'Single Layer FBE',
    ,
    'Dual Layer FBE',
    'Others',
  ];

  const materialOptions = {
    FBE: [
      'Pipeclad 2000 Slow Gel',
      'Pipeclad 2000 LAT',
      'Pipeclad Hot 150',
      'Pipeclad Hot 150 Flex',
      'Pipeclad Hot 12',
      'Pipeclad Hot 120 Flex',
      'Axalta Nap-Gard',
      'Jotapipe AC 1003 XT',
    ],
    'Liquid Epoxy': ['Canusa Liquid Epoxy Type P'],
    'Single Layer FBE': ['Axalta Nap-Gard 72555LG'],
    'Dual Layer FBE': ['Axalta Nap-Gard 7-2675'],
    // 'Others': ['One-stage', 'Two-stage'],
  };

  const predefinedTemperatureRanges = {
    'Pipeclad 2000 Slow Gel': { min: 205, max: 240 },
    'Pipeclad 2000 LAT': { min: 175, max: 210 },
    'Pipeclad Hot 150': { min: 204, max: 240 },
    'Pipeclad Hot 150 Flex': { min: 204, max: 240 },
    'Pipeclad Hot 12': { min: 205, max: 240 },
    'Pipeclad Hot 120 Flex': { min: 205, max: 240 },
    'Canusa Liquid Epoxy Type P': { min: 175, max: 210, minS1: 30, maxS1: 60 },
    'Axalta Nap-Gard': { min: 218, max: 239, minS1: 0, maxS1: 180 },
    'Axalta Nap-Gard 72555LG': { min: 225, max: 239 },
    'Axalta Nap-Gard 7-2675': { min: 218, max: 239 },
    'Jotapipe AC 1003 XT': { min: 232, max: 250 },
  };

  const { Column, ColumnGroup } = Table;
  const { Title } = Typography;
  const [formData, setFormData] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFile, setOutputFile] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [heatProfilelist, setHeatProfilelist] = useState([]);
  const [heatProfilelistModalVisible, setHeatProfilelistModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form] = Form.useForm();
  const [listId, setListId] = useState('');
  const [isOtherMaterialClass, setIsOtherMaterialClass] = useState(false);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailListId, setEmailListId] = useState('');
  const [shareButtonVisible, setShareButtonVisible] = useState(false);
  const [pdfReportDetails, setPDFReportDetails] = useState([]);
  const [textFileDetails, setTextFileDetails] = useState([]);
  const [inputFile, setInputFile] = useState('');
  const [fileList, setFileList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchKeyExist, setSearchKeyExist] = useState('');
  const [sortingKeys, setSortingKeys] = useState('');

  const [searchCriteria, setSearchCriteria] = useState({});
  const [filterTrialDate, setFilterTrialDate] = useState(null);
  const [filterCreatedDate, setFilterCreatedDate] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    reportAuthor: '',
    material: '',
  });
  const [columnFilterDropdownOpen, setColumnFilterDropdownOpen] = useState(null); // dataIndex of open column or null
  const [columnFilterConditions, setColumnFilterConditions] = useState({
    reportNumber: 'contains',
    projectName: 'contains',
    projectNumber: 'contains',
    reportAuthor: 'contains',
    material: 'contains',
  });
  const [columnSelectedValues, setColumnSelectedValues] = useState({
    reportNumber: null,
    projectName: null,
    projectNumber: null,
    reportAuthor: null,
    material: null,
  });
  const [columnValueSearch, setColumnValueSearch] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    reportAuthor: '',
    material: '',
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  /** Full page(s) of rows for column filter facets — not limited to current table page */
  const [filterFacetRows, setFilterFacetRows] = useState([]);
  const [filterFacetLoading, setFilterFacetLoading] = useState(false);
  const [signatureReq, setSignatureReq] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentPagesize, setCurrentPagesize] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [copyModelVisible, setCopyModelVisible] = useState(false);
  //const [listCount, setListCount] = useState(0);
  const [currentImage, setCurrentImage] = useState(1);
  const [images, setImage] = useState([]);
  const [steelODUnit, setSteelODUnit] = useState('mm');
  const [wtUnits, setWtUnits] = useState('mm');
  const [coilLengthUnit, setCoilLengthUnit] = useState('mm');
  const [cableUnit, setcableUnit] = useState('meters');
  const [parentCoatingThicknessUnit, setParentCoatingThicknessUnit] = useState('mm');
  const [editedFields, setEditedFields] = useState({});
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isInputfeildChanged, setIsInputfeildChanged] = useState(false);
  const [OpenSignaturePad, setOpenSignaturePad] = useState(false);
  const [SignatureSaveLoading, setSignatureSaveLoading] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [SignatureData, setSignatureData] = useState('');
  const [ReviewAndSign, setReviewAndSign] = useState(false);

  // Helper function to render PDF page as image using PDF.js
  const renderPdfPageAsImage = async (pdfBytes, pageNumber) => {
    try {
      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(pdfBytes),
        useSystemFonts: true,
        verbosity: 0 // Suppress warnings
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(pageNumber);

      // High-quality raster rendering for combined PDF output
      const viewport = page.getViewport({ scale: 2.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render the page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      // Convert canvas to data URL
      return canvas.toDataURL('image/png');
    } catch (error) {
      return null;
    }
  };

  // Initial list load: handled by the [searchCriteria] effect below (avoids duplicate fetch on mount).

  // Table columns with proper header labels and min widths for horizontal scroll
  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPageNumber(1);
  };

  /* Client-side sorted data for Heat Profile table (backend does not support sort) */
  const sortedProjectList = useMemo(() => {
    if (!projectList || !Array.isArray(projectList) || !sortField || !sortOrder) {
      return projectList ? [...projectList] : [];
    }
    const isDesc = sortOrder === 'descend';
    const dateKeys = ['trailDate', 'createdAt'];
    return [...projectList].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (dateKeys.includes(sortField)) {
        aVal = aVal ? moment(aVal).valueOf() : 0;
        bVal = bVal ? moment(bVal).valueOf() : 0;
        return isDesc ? bVal - aVal : aVal - bVal;
      }
      aVal = aVal == null ? '' : String(aVal);
      bVal = bVal == null ? '' : String(bVal);
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return isDesc ? -cmp : cmp;
    });
  }, [projectList, sortField, sortOrder]);

  const FILTERABLE_COLUMNS = ['reportNumber', 'projectName', 'projectNumber', 'reportAuthor', 'material'];

  const HEAT_PROFILE_FACET_PAGE_CAP = 10000;

  /** Multiple facet values = OR semantics; API does not accept comma-separated lists, so we bulk-fetch and filter client-side. */
  const hasMultiFacetSelection = useMemo(
    () =>
      FILTERABLE_COLUMNS.some((col) => {
        const s = columnSelectedValues[col];
        return s != null && s.size > 1;
      }),
    [columnSelectedValues]
  );

  /** OR-match facet checkboxes on the current row set (after API returns a broad page when multi-facet is active). */
  const displayedProjectList = useMemo(() => {
    let list = sortedProjectList || [];
    FILTERABLE_COLUMNS.forEach((col) => {
      const sel = columnSelectedValues[col];
      if (sel != null && sel.size > 0) {
        list = list.filter((row) => {
          const v = get(row, col);
          const s = v == null ? '' : String(v).trim();
          return sel.has(s);
        });
      }
    });
    return list;
  }, [sortedProjectList, columnSelectedValues]);

  /** Multi-facet + many matches: paginate client-side (Table does not slice dataSource). */
  const heatProfileTableRows = useMemo(() => {
    if (hasMultiFacetSelection && displayedProjectList.length > 25) {
      const start = (currentPageNumber - 1) * currentPagesize;
      return displayedProjectList.slice(start, start + currentPagesize);
    }
    return displayedProjectList;
  }, [
    hasMultiFacetSelection,
    displayedProjectList,
    currentPageNumber,
    currentPagesize,
  ]);

  /** Latest list/search for other features — facet list is snapshotted only when a column filter opens (see effect below). */
  const facetFetchContextRef = useRef({
    searchCriteria: {},
    listCount: null,
    projectList: [],
  });
  facetFetchContextRef.current = {
    searchCriteria:
      searchCriteria && typeof searchCriteria === 'object' ? { ...searchCriteria } : {},
    listCount,
    projectList: Array.isArray(projectList) ? projectList : [],
  };

  /**
   * Load facet rows once when the user opens a column filter (not on checkbox / searchCriteria changes).
   * Otherwise every facet checkbox tick re-fetched and toggled "Loading values…".
   */
  useEffect(() => {
    if (!columnFilterDropdownOpen) {
      setFilterFacetRows([]);
      setFilterFacetLoading(false);
      return;
    }
    if (!FILTERABLE_COLUMNS.includes(columnFilterDropdownOpen)) {
      setFilterFacetRows([]);
      setFilterFacetLoading(false);
      return;
    }

    const snap = facetFetchContextRef.current;
    const sc = snap.searchCriteria && typeof snap.searchCriteria === 'object' ? snap.searchCriteria : {};
    const pl = Array.isArray(snap.projectList) ? snap.projectList : [];
    const fromListCount = snap.listCount != null && Number(snap.listCount) > 0 ? Number(snap.listCount) : 0;
    const fromRowTotal =
      pl.length > 0 && pl[0] && pl[0].totalCount != null ? Number(pl[0].totalCount) : 0;
    const total = fromListCount || fromRowTotal || Math.max(pl.length, 1);
    const pageSize = Math.min(Math.max(total, 1), HEAT_PROFILE_FACET_PAGE_CAP);

    let cancelled = false;
    setFilterFacetLoading(true);

    const params = new URLSearchParams();
    params.set('pageNumber', '1');
    params.set('pageSize', String(pageSize));
    if (Object.keys(sc).length > 0) {
      params.set('searchKey', encodeURIComponent(JSON.stringify(sc)));
    }

    (async () => {
      try {
        const res = await axiosConfig.get(`/technical-heat-profile?${params.toString()}`);
        const rows = get(res, ['data', 'data'], []);
        if (!cancelled) {
          setFilterFacetRows(Array.isArray(rows) ? rows : []);
        }
      } catch {
        if (!cancelled) setFilterFacetRows([]);
      } finally {
        if (!cancelled) setFilterFacetLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [columnFilterDropdownOpen]);

  const distinctColumnValues = useMemo(() => {
    const useFacets =
      columnFilterDropdownOpen && filterFacetRows.length > 0 && !filterFacetLoading;
    const list = useFacets ? filterFacetRows : sortedProjectList || [];
    const out = {};
    FILTERABLE_COLUMNS.forEach((col) => {
      const set = new Set();
      list.forEach((row) => {
        const v = get(row, col);
        const s = v == null ? '' : String(v).trim();
        set.add(s);
      });
      out[col] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    });
    return out;
  }, [
    sortedProjectList,
    filterFacetRows,
    filterFacetLoading,
    columnFilterDropdownOpen,
  ]);

  const FILTER_CONDITION_OPTIONS = [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'startsWith', label: 'Starts with' },
    { value: 'endsWith', label: 'Ends with' },
    { value: 'notContains', label: 'Does not contain' },
  ];

  const handleColumnFilterConditionChange = (dataIndex, value) => {
    setColumnFilterConditions((prev) => ({ ...prev, [dataIndex]: value }));
  };

  const handleColumnValueSearchChange = (dataIndex, value) => {
    setColumnValueSearch((prev) => ({ ...prev, [dataIndex]: value }));
  };

  const toggleColumnSelectedValue = (dataIndex, value, checked) => {
    setColumnSelectedValues((prev) => {
      const next = { ...prev };
      const current = next[dataIndex];
      const set = current == null ? new Set(distinctColumnValues[dataIndex] || []) : new Set(current);
      if (checked) set.add(value);
      else set.delete(value);
      next[dataIndex] = set.size === (distinctColumnValues[dataIndex]?.length || 0) ? null : set;
      return next;
    });
  };

  const handleSelectAllColumnValues = (dataIndex, checked) => {
    setColumnSelectedValues((prev) => ({ ...prev, [dataIndex]: checked ? null : new Set() }));
  };

  const resetColumnFilterPanel = (dataIndex) => {
    handleColumnFilterChange(dataIndex, '');
    handleColumnValueSearchChange(dataIndex, '');
    handleColumnFilterConditionChange(dataIndex, 'contains');
    setColumnSelectedValues((prev) => ({ ...prev, [dataIndex]: null }));
  };

  const isTextColumnFilterActive = (dataIndex) => {
    if ((columnFilters[dataIndex] || '').trim()) return true;
    const sel = columnSelectedValues[dataIndex];
    const d = distinctColumnValues[dataIndex] || [];
    if (sel != null && sel.size > 0 && sel.size < d.length) return true;
    return false;
  };

  const renderTextColumnFilterTrigger = (dataIndex) => (
    <Dropdown
      visible={columnFilterDropdownOpen === dataIndex}
      onVisibleChange={(open) => setColumnFilterDropdownOpen(open ? dataIndex : null)}
      overlay={renderColumnFilterOverlay(dataIndex)}
      trigger={['click']}
      overlayClassName="heat-profile-column-filter-overlay"
      getPopupContainer={() => document.body}
    >
      <span
        role="button"
        tabIndex={0}
        className={`heat-profile-column-filter-icon-wrap ${isTextColumnFilterActive(dataIndex) ? 'has-active' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        aria-label={`Filter column ${dataIndex}`}
      >
        <Filter size={14} strokeWidth={2} className="heat-profile-column-filter-lucide" aria-hidden />
      </span>
    </Dropdown>
  );

  const renderColumnFilterOverlay = (dataIndex) => {
    const distinct = distinctColumnValues[dataIndex] || [];
    const search = (columnValueSearch[dataIndex] || '').toLowerCase();
    const filteredValues = search
      ? distinct.filter((v) => String(v).toLowerCase().includes(search))
      : distinct;
    const selectedSet = columnSelectedValues[dataIndex];
    const allSelected = selectedSet == null;

    return (
      <div
        className="heat-profile-column-filter-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="heat-profile-column-filter-dropdown-body">
          {filterFacetLoading && (
            <div className="heat-profile-column-filter-loading">
              <Spin size="small" />
              <span>Loading values…</span>
            </div>
          )}
          <div className="heat-profile-column-filter-group heat-profile-column-filter-group-condition">
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <CustomSelect
                className="heat-profile-column-filter-condition-select"
                value={columnFilterConditions[dataIndex] || 'contains'}
                onChange={(v) => handleColumnFilterConditionChange(dataIndex, v)}
                options={FILTER_CONDITION_OPTIONS}
                placeholder="Condition"
              />
            </div>
          </div>
          <div className="heat-profile-column-filter-group">
            <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm">
              <Input
                placeholder="Filter..."
                size="small"
                value={columnFilters[dataIndex] || ''}
                onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
              />
            </div>
          </div>
          <div className="heat-profile-column-filter-group">
            <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm">
              <Input
                placeholder="Search..."
                size="small"
                value={columnValueSearch[dataIndex] || ''}
                onChange={(e) => handleColumnValueSearchChange(dataIndex, e.target.value)}
              />
            </div>
          </div>
          <div className="heat-profile-column-filter-values">
            <Checkbox
              className="heat-profile-column-filter-crc-select-all"
              label="(Select All)"
              checked={allSelected}
              onChange={(checked) => handleSelectAllColumnValues(dataIndex, checked)}
              borderColor="#00C9A7"
              tickColor="#00C9A7"
            />
            <div className="heat-profile-column-filter-value-list">
              {filteredValues.map((val) => {
                const isChecked = selectedSet == null || selectedSet.has(val);
                const rowLabel = val === '' ? '(Blank)' : String(val);
                return (
                  <div key={String(val)} className="heat-profile-column-filter-value-item">
                    <Checkbox
                      className="heat-profile-column-filter-crc-row"
                      label={rowLabel}
                      checked={isChecked}
                      onChange={(checked) =>
                        toggleColumnSelectedValue(dataIndex, val, checked)
                      }
                      borderColor="#00C9A7"
                      tickColor="#00C9A7"
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="heat-profile-column-filter-footer">
            <Button type="link" size="small" onClick={() => resetColumnFilterPanel(dataIndex)}>
              Reset
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderColumnTitle = (title, dataIndex) => (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper">
        <span>{title}</span>
        <span className="custom-sort-icon">
          {sortField === dataIndex && sortOrder === 'ascend' && <CaretUpOutlined />}
          {sortField === dataIndex && sortOrder === 'descend' && <CaretDownOutlined />}
          {(!sortField || sortField !== dataIndex || !sortOrder) && (
            <span className="sort-icon-placeholder">
              <CaretUpOutlined />
            </span>
          )}
        </span>
        {renderTextColumnFilterTrigger(dataIndex)}
      </div>
      <div className="header-filter-input">
        <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
          <Input
            placeholder="Search"
            size="small"
            value={columnFilters[dataIndex] ?? ''}
            onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  );

  // Table columns with per-column filters
  const columns = [
    {
      title: renderColumnTitle('REPORT NUMBER', 'reportNumber'),
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      align: 'left',
      width: 180,
      sorter: true,
      sortOrder: sortField === 'reportNumber' ? sortOrder : undefined,
    },
    {
      title: renderColumnTitle('PROJECT NAME', 'projectName'),
      dataIndex: 'projectName',
      key: 'projectName',
      align: 'left',
      width: 200,
      sorter: true,
      sortOrder: sortField === 'projectName' ? sortOrder : undefined,
    },
    {
      title: renderColumnTitle('PROJECT NUMBER', 'projectNumber'),
      dataIndex: 'projectNumber',
      key: 'projectNumber',
      align: 'left',
      width: 170,
      sorter: true,
      sortOrder: sortField === 'projectNumber' ? sortOrder : undefined,
    },
    {
      title: renderColumnTitle('REPORT AUTHOR', 'reportAuthor'),
      dataIndex: 'reportAuthor',
      key: 'reportAuthor',
      align: 'left',
      width: 190,
      sorter: true,
      sortOrder: sortField === 'reportAuthor' ? sortOrder : undefined,
    },
    {
      title: renderColumnTitle('MATERIAL', 'material'),
      dataIndex: 'material',
      key: 'material',
      align: 'left',
      width: 220,
      sorter: true,
      sortOrder: sortField === 'material' ? sortOrder : undefined,
      render: (text) => (text ? text : '---'),
    },
    {
      title: (
        <div className="column-header-with-filter">
          <div className="column-header-text-wrapper">
            <span>TRIAL DATE</span>
            <span className="custom-sort-icon">
              {sortField === 'trailDate' && sortOrder === 'ascend' && <CaretUpOutlined />}
              {sortField === 'trailDate' && sortOrder === 'descend' && <CaretDownOutlined />}
              {(!sortField || sortField !== 'trailDate' || !sortOrder) && (
                <span className="sort-icon-placeholder">
                  <CaretUpOutlined />
                </span>
              )}
            </span>
          </div>
          <div className="header-filter-input">
            <DatePicker
              className="heat-profile-header-date-picker"
              placeholder="Select date"
              size="small"
              format="DD-MMM-YYYY"
              value={filterTrialDate}
              onChange={(date) => setFilterTrialDate(date)}
              onClick={(e) => e.stopPropagation()}
              suffixIcon={
                filterTrialDate ? (
                  <CloseOutlined
                    className="heat-profile-date-clear-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterTrialDate(null);
                    }}
                  />
                ) : (
                  <CalendarOutlined className="heat-profile-date-icon" />
                )
              }
              dropdownClassName="heat-profile-datepicker-dropdown"
              allowClear={false}
            />
          </div>
        </div>
      ),
      dataIndex: 'trailDate',
      key: 'trailDate',
      align: 'center',
      width: 190,
      sorter: true,
      sortOrder: sortField === 'trailDate' ? sortOrder : undefined,
      render: (text) => moment(text).format('YYYY MMM DD'),
    },
    {
      title: (
        <div className="column-header-with-filter">
          <div className="column-header-text-wrapper">
            <span>CREATE DATE</span>
            <span className="custom-sort-icon">
              {sortField === 'createdAt' && sortOrder === 'ascend' && <CaretUpOutlined />}
              {sortField === 'createdAt' && sortOrder === 'descend' && <CaretDownOutlined />}
              {(!sortField || sortField !== 'createdAt' || !sortOrder) && (
                <span className="sort-icon-placeholder">
                  <CaretUpOutlined />
                </span>
              )}
            </span>
          </div>
          <div className="header-filter-input">
            <DatePicker
              className="heat-profile-header-date-picker"
              placeholder="Select date"
              size="small"
              format="DD-MMM-YYYY"
              value={filterCreatedDate}
              onChange={(date) => setFilterCreatedDate(date)}
              onClick={(e) => e.stopPropagation()}
              suffixIcon={
                filterCreatedDate ? (
                  <CloseOutlined
                    className="heat-profile-date-clear-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterCreatedDate(null);
                    }}
                  />
                ) : (
                  <CalendarOutlined className="heat-profile-date-icon" />
                )
              }
              dropdownClassName="heat-profile-datepicker-dropdown"
              allowClear={false}
            />
          </div>
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 190,
      sorter: true,
      sortOrder: sortField === 'createdAt' ? sortOrder : undefined,
      render: (text) => moment(text).format('YYYY MMM DD'),
    },
    {
      title: 'FILES',
      dataIndex: 'files',
      key: 'files',
      align: 'center',
      width: 150,
      render: (text, record) => (
        <div className="heat-profile-files-cell heat-profile-actions-cell">
          <Tooltip title="View PDF">
            <FileIcon
              className="txtIcon lucide"
              size={24}
              strokeWidth={2}
              onClick={() => window.open(`/heat-profile/pdf/${record.id}`, '_blank')}
            />
          </Tooltip>
          <Tooltip title="View Text File">
            <FileText
              className="txtIcon lucide"
              size={24}
              strokeWidth={2}
              onClick={() => window.open(record.textFileDetails?.s3Url, '_blank')}
            />
          </Tooltip>
          {record.imageFileDetails?.s3Url && (
            <Tooltip title="Image File">
              <LucideImage
                className="txtIcon lucide"
                size={24}
                strokeWidth={2}
                onClick={() => window.open(`/heat-profile/image/${record.id}`, '_blank')}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      align: 'center',
      width: 160,
      render: (text, record) => (
        <div className="heat-profile-actions-cell">
          <Tooltip title="Edit">
            <Pencil
              className="txtIcon lucide"
              size={24}
              strokeWidth={2}
              onClick={() => showAddEditListModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Copy">
            <Copy
              className="txtIcon lucide"
              size={24}
              strokeWidth={2}
              onClick={() => showCopyEditListModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Share">
            <Share2
              className="txtIcon lucide"
              size={24}
              strokeWidth={2}
              onClick={() => showSentEmailModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Trash2
              className="txtIcon lucide"
              size={24}
              strokeWidth={2}
              onClick={() => showDeleteModal(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];
  // // const getHeatProfileList = async () => {
  // //     // setShowLoader(true);

  // //     // try {
  // //     //     const response = await axiosConfig.get('/technical-heat-profile');

  // //     //     if (response && response.data.data.length) {

  // //     //         setListCount(response.data.data[0].totalCount ?? 0)
  // //     //         setHeatProfilelist(response.data.data);
  // //     //         setFilteredList(response.data.data);
  // //     //     } else {
  // //     //         notification.error({
  // //     //             message: 'No data available.',
  // //     //         });
  // //     //         setListCount(0)
  // //     //     }
  // //     // } catch (error) {
  // //     //     console.error('Error fetching heat profile list:', error);

  // //     //     notification.error({
  // //     //         message: 'Something went wrong. Please try again later.',
  // //     //     });

  // //     // } finally {
  // //     //     setShowLoader(false);
  // //     // }
  // // };

  // // useEffect(() => {
  // //     getHeatProfileList();
  // // }, [])

  // //search heat profile list by  onSearchByReportNumber
  // const onSearchByReportNumber = (e) => {
  //     let data = e.target.value.toLowerCase();
  //     setSearchKeyExist(data);
  //     const filterList = heatProfilelist.filter((item) =>
  //         item.reportNumber.toLowerCase().includes(data)
  //     );

  //     setFilteredList(filterList);
  // };

  // //search heat profile list by project name
  // const onSearchByProjectName = (e) => {
  //     let data = e.target.value.toLowerCase();
  //     setSearchKeyExist(data);
  //     const filterList = heatProfilelist.filter((item) =>
  //         item.projectName.toLowerCase().includes(data)
  //     );

  //     setFilteredList(filterList);
  // };

  // //search heat profile list by onSearchByProjectNumber
  // const onSearchByProjectNumber = (e) => {
  //     let data = e.target.value.toLowerCase();
  //     setSearchKeyExist(data);
  //     const filterList = heatProfilelist.filter((item) =>
  //         item.projectNumber.toLowerCase().includes(data)
  //     );

  //     setFilteredList(filterList);
  // };

  // //search heat profile list By ReportAuthor
  // const onSearchByReportAuthor = (e) => {
  //     let data = e.target.value.toLowerCase();
  //     setSearchKeyExist(data);

  //     const filterList = heatProfilelist.filter((item) =>
  //         item.reportAuthor.toLowerCase().includes(data)
  //     );

  //     setFilteredList(filterList);
  // };

  //search heat profile list by Material
  // const onSearchByMaterial = (e) => {
  //     let data = e.target.value.toLowerCase();
  //     setSearchKeyExist(data);

  //     const filterList = heatProfilelist.filter((item) =>
  //         item.material.toLowerCase().includes(data)
  //     );

  //     setFilteredList(filterList);
  // };
  const handleReviewAndSign = async () => {
    setReviewAndSign(true);
  };

  //email popup maintain
  const handleAddEmailClick = async () => {
    setEmailModalVisible(true);
  };

  const handleEmailModalCancel = () => {
    setEmailModalVisible(false);
    setEmailListId('');
  };

  const showSentEmailModal = (record) => {
    setEmailModalVisible(true);
    setEmailListId(record.id);
  };

  //get textFile from s3 and set in input field
  const handleDownloadAndSetFile = async (s3KeysValue) => {
    const response = await fetch(s3KeysValue.s3Url);
    const blob = await response.blob();

    const downloadedFile = new File([blob], s3KeysValue.fileName);
    setFileList(['abc.txt']);
    setInputFile(s3KeysValue.s3Url);
    setSelectedFile(downloadedFile);
  };
  //set stat with Updated List Data
  const showAddEditListModal = async (record, edit) => {
    let userDt = {};
    if (record !== '') {
      const momentProps = ['trailDate'];
      userDt = Object.fromEntries(
        Object.entries(record).map(([key, value]) => [
          key,
          momentProps.includes(key) ? moment(value) : value,
        ])
      );

      if (userDt.stages == 1.0) {
        userDt.stages = 1;
      } else {
        userDt.stages = 2;
      }
      form.setFieldsValue(userDt);

      if (userDt.trailDate) {
        userDt.trailDate = moment(userDt.trailDate).format('YYYY-MM-DD');
      }
      if (!materialClassOptions.includes(userDt.materialClass)) {
        setIsOtherMaterialClass(true);
      }
      if (!record.units) {
        userDt.units = formData.units;
      }
      if (!record.required) {
        userDt.required = formData.required;
      }
      formData.pdfReportDetails = {};

      setFormData({ ...formData, ...userDt });

      // setOutputFile(record.pdfReportDetails.s3Url);
      setListId(record.id);
      await handleDownloadAndSetFile(record.textFileDetails);
    } else {
      handleReset();
    }
    setIsInputfeildChanged(false);
    setIsEdit(edit);
    setHeatProfilelistModalVisible(true);
  };

  //set stat with Updated List Data
  const showCopyEditListModal = async (record, edit) => {
    let userDt = {};
    if (record !== '') {
      const momentProps = ['trailDate'];
      userDt = Object.fromEntries(
        Object.entries(record).map(([key, value]) => [
          key,
          momentProps.includes(key) ? moment(value) : value,
        ])
      );

      if (userDt.stages == 1.0) {
        userDt.stages = 1;
      } else {
        userDt.stages = 2;
      }
      userDt.reportAuthor = name ?? userDt.reportAuthor;
      form.setFieldsValue(userDt);

      if (userDt.trailDate) {
        userDt.trailDate = moment(userDt.trailDate).format('YYYY-MM-DD');
      }
      if (!materialClassOptions.includes(userDt.materialClass)) {
        setIsOtherMaterialClass(true);
      }
      if (!record.units) {
        userDt.units = formData.units;
      }
      if (!record.required) {
        userDt.required = formData.required;
      }
      delete userDt['id'];

      formData.imageFileDetails = {};
      formData.pdfReportDetails = {};

      setFormData({ ...formData, ...userDt });
      // setOutputFile(record.pdfReportDetails.s3Url);
      setListId(record.id);
      //await handleDownloadAndSetFile(record.textFileDetails)
    } else {
      handleReset();
    }
    setCopyModelVisible(edit);
    setHeatProfilelistModalVisible(true);
  };

  //update List  data
  const updateListData = async (reportUrlObject, txtFileInfo) => {
    setLoading(true);
    const payload = {
      heatProfile: {
        ...omit(formData, 'customMaterialClass'),
        pdfReportDetails: reportUrlObject.Report_s3Url,
        textFileDetails: txtFileInfo,
        imageFileDetails: reportUrlObject.Image_s3Url,
      },
    };

    try {
      const response = await axiosConfig.put(`/technical-heat-profile/${listId}`, payload);
      message.success(`Data updated successfully`);
      setEmailListId(listId);
      setShareButtonVisible(true);

      if (formData['signatureRequired'] && formData['signatureRequired'] == true) {
        setIsGenerated(true);
      }
      //getHeatProfileList();
      var obj = {
        pageSize: currentPagesize,
        pageNumber: currentPageNumber,
        searchKey: searchCriteria,
      };
      await onProjectList(obj);
      await NotifyEmails(formData, isEdit);
    } catch (error) {
      console.error('Error submitting data:', error);
      message.error(`Data update failed.`);
    }
    setLoading(false);
    //setIsEdit(false)
  };

  //Delete List data
  const handleDeletion = async (record) => {
    Modal.confirm({
      className: 'custom-text-clr',
      title: `Are you sure you want to delete  report number ${record.reportNumber} data`,
      async onOk() {
        try {
          const response = await axiosConfig.delete(`/technical-heat-profile/${record.id}`);
          if (response.status === 200) {
            message.success('Data deleted successfully');
            let obj = {
              pageSize: currentPagesize,
              pageNumber: currentPageNumber,
              searchKey: searchCriteria,
            };
            await onProjectList(obj);
          } else {
            throw new Error('Failed to delete data. Please try again.');
          }
        } catch (error) {
          console.error('Error deleting data:', error);
          message.error('Data delete failed.');
          throw error;
        }
      },
      onCancel() { },
    });
  };
  /** Display Confirmation while deleting */
  const showDeleteModal = (record) => {
    setIsDeleteModalVisible(true);
    setSelectedData(record);
  };
  /** Deleting Spare parts data on confirm */
  const handleDeleteOk = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await axiosConfig.delete(`/technical-heat-profile/${selectedData.id}`);
      if (response.status === 200) {
        message.success('Data deleted successfully');
        let obj = {
          pageSize: currentPagesize,
          pageNumber: currentPageNumber,
          searchKey: searchCriteria,
        };
        await onProjectList(obj);
      } else {
        throw new Error('Failed to delete data. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting data:', error);
      message.error('Data delete failed.');
      throw error;
    }
    setIsDeleteLoading(false);
    setIsDeleteModalVisible(false);
    setSelectedData('');
  };
  /** Cancelling the deletion of spare parts data */
  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedData('');
  };

  //TextFile conversion api call with Data
  const handleChange = async (info) => {
    const invalidChars = /[<>:"/\\|?*']/;
    const { status, name, originFileObj } = info.file;

    if (status) {
      if (invalidChars.test(name)) {
        notification.warning({
          message: 'Invalid File Name',
          description: 'The file name contains invalid characters: < > : " / \\ | ? * \'',
        });
        // Optionally, you can clear the file list if an invalid file is selected
        setFileList([]);
      } else {
        setSelectedFile(originFileObj);
        setFileList([info.file]);
        setIsInputfeildChanged(true);
      }
    } else if (status === 'error') {
      console.error(`${name} file upload failed.`);
    }
  };
  const OngenrateSignedPdf = async (category, type, values) => {
    //console.log(category, type, values)
    //setFormData((prevData) => ({ ...prevData, [key,keyValue]: imageData }));
    setSignatureData((prevOptions) => {
      return {
        ...prevOptions,
        [category]: {
          ...prevOptions[category],
          [type]: values,
        },
      };
    });
  };
  const GeneratePDFWith_sign = async () => {
    if (
      (SignatureData['preparedBySign'] &&
        ((SignatureData['preparedBySign'].Image && !SignatureData['preparedBySign'].Name) ||
          (!SignatureData['preparedBySign'].Image && SignatureData['preparedBySign'].Name))) ||
      (SignatureData['approvedBySign'] &&
        ((SignatureData['approvedBySign'].Image && !SignatureData['approvedBySign'].Name) ||
          (!SignatureData['approvedBySign'].Image && SignatureData['approvedBySign'].Name))) ||
      (SignatureData['approved2BySign'] &&
        ((SignatureData['approved2BySign'].Image && !SignatureData['approved2BySign'].Name) ||
          (!SignatureData['approved2BySign'].Image && SignatureData['approved2BySign'].Name)))
    ) {
      message.error(`Please fill the requred feild`);
      return;
    }

    setSignatureSaveLoading(true);

    // console.log('SingaureData', SignatureData)

    if (selectedFile && SignatureData !== '') {
      const updatedFormData = new FormData();
      const heatprofiledata = {};

      // Helper function to append units to specific keys
      const appendUnit = (key, value) => `${value} ${formData.units[key]}`;

      // Iterate over formData entries
      Object.entries(formData).forEach(([key, value]) => {
        // Handle specific keys requiring unit appending
        const unitKeys = ['steelOD', 'coilLength', 'wt', 'cableLength', 'parentCoatingThickness'];
        if (unitKeys.includes(key)) {
          value = appendUnit(key, value);
        }

        // Handle boolean conversion for `signatureRequired`
        if (key === 'signatureRequired' && value === false) {
          value = 'False';
        }

        // Handle JSON conversion for `required`
        if (key === 'required') {
          value = JSON.stringify(value);
        }

        // Handle JSON conversion for `units`
        if (key === 'units') {
          value = JSON.stringify(value);
        }

        // Update heatprofiledata and FormData
        heatprofiledata[key] = value;
        updatedFormData.append(key, value);
      });

      Object.entries(SignatureData).forEach(([key, value]) => {
        heatprofiledata[key] = value;

        if (key == 'preparedBySign') {
          if (SignatureData.preparedBySign['Image'] && !value.Date) {
            value.Date = moment().utc().format('DD-MMM-YYYY HH:mm');
            SignatureData.preparedBySign['Date'] = value.Date;
          }

          value = JSON.stringify(value);
        }

        if (key == 'approvedBySign') {
          if (SignatureData.approvedBySign['Image'] && !value.Date) {
            (value.Date = moment().utc().format('DD-MMM-YYYY HH:mm')),
              (SignatureData.approvedBySign['Date'] = value.Date);
          }
          value = JSON.stringify(value);
        }
        if (key == 'approved2BySign') {
          if (SignatureData.approved2BySign['Image'] && !value.Date) {
            (value.Date = moment().utc().format('DD-MMM-YYYY HH:mm')),
              (SignatureData.approved2BySign['Date'] = value.Date);
          }
          value = JSON.stringify(value);
        }

        updatedFormData.append(key, value);
      });

      //console.log(heatprofiledata, SignatureData)

      // Add missing fields that are required by the API
      updatedFormData.append('imageFileDetails', JSON.stringify(formData.imageFileDetails || {}));
      updatedFormData.append('textFileDetails', JSON.stringify(formData.textFileDetails || {}));
      updatedFormData.append('pdfReportDetails', JSON.stringify(formData.pdfReportDetails || {}));

      // Add ID if editing
      if (isEdit && listId) {
        updatedFormData.append('id', listId);
      }

      // Add timestamps
      updatedFormData.append('createdAt', formData.createdAt || moment().format('YYYY-MM-DD'));
      updatedFormData.append('updatedAt', moment().format('YYYY-MM-DD'));
      updatedFormData.append('totalCount', formData.totalCount || '0');

      updatedFormData.append('file', selectedFile);
      if (formData.units.GeneratedDate)
        updatedFormData.append('GeneratedDate', formData.units.GeneratedDate);

      {
        {
          // const txtFileInfo = await uploadFileToS3(selectedFile);
          // const response = await axios.post('http://127.0.0.1:5000/generate-report', updatedFormData, {
          //     headers: {

          //         'Content-Type': 'multipart/form-data',
          //     },
          // });

          const response = await axios.post(
            process.env.REACT_APP_AWS_HEAT_PROFILE_REPORT_GENERATOR,
            updatedFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          const reportUrlObject = response.data.report_url;

          // Mark the report as signed
          reportUrlObject.Report_s3Url['IsSigned'] = true;

          // Add `preparedBySign` if both Image and Name are present
          if (
            SignatureData['preparedBySign'] &&
            SignatureData['preparedBySign']?.Image &&
            SignatureData['preparedBySign']?.Name
          ) {
            reportUrlObject.Report_s3Url['preparedBySign'] = {
              ...SignatureData['preparedBySign'],
              IsSigned: true, // Add `IsSigned` directly to the object
            };
          }

          // Add `approvedBySign` if both Image and Name are present
          if (
            SignatureData['approvedBySign'] &&
            SignatureData['approvedBySign']?.Image &&
            SignatureData['approvedBySign']?.Name
          ) {
            reportUrlObject.Report_s3Url['approvedBySign'] = {
              ...SignatureData['approvedBySign'],
              IsSigned: true, // Add `IsSigned` directly to the object
            };
          }

          // Add `approved2BySign` if both Image and Name are present
          if (
            SignatureData['approved2BySign'] &&
            SignatureData['approved2BySign']?.Image &&
            SignatureData['approved2BySign']?.Name
          ) {
            reportUrlObject.Report_s3Url['approved2BySign'] = {
              ...SignatureData['approved2BySign'],
              IsSigned: true, // Add `IsSigned` directly to the object
            };
          }
          setPDFReportDetails({ ...reportUrlObject.Report_s3Url });
          setOutputFile(reportUrlObject.Report_s3Url.s3Url);

          const payload = {
            heatProfile: {
              ...omit(formData, 'customMaterialClass'),
              pdfReportDetails: reportUrlObject.Report_s3Url,
            },
          };
          try {
            const response = await axiosConfig.put(`/technical-heat-profile/${listId}`, payload);
            message.success(`Data updated successfully`);

            var obj = {
              pageSize: currentPagesize,
              pageNumber: currentPageNumber,
              searchKey: searchCriteria,
            };
            await onProjectList(obj);
          } catch (error) {
            console.error('Error submitting data:', error);
            message.error(`Data update failed.`);
          }
          setOpenSignaturePad(false);
          closeDrawer();
          //setIsEdit(false)
        }
      }
    }
    //  handleReset();
    setSignatureSaveLoading(false);
  };

  const uploadFileToS3 = async (selectedFile) => {
    try {
      const textFileTos3Response = await axiosConfig.get(
        `/technical-heat-profile/signed-url?fileName=${selectedFile.name}&deployEnv=${process.env.REACT_APP_STAGE}`
      );
      if (textFileTos3Response.status === 200) {
        const textFileTos3 = textFileTos3Response.data;
        const uploadPresignedUrlResponse = await axios.put(
          textFileTos3.data.preSignedUrl,
          selectedFile,
          {
            headers: {
              'Content-Disposition': 'attachment',
              'Content-Type': 'text/plain',
            },
          }
        );

        if (uploadPresignedUrlResponse.status === 200) {
          const fileInfo = {
            s3Url: textFileTos3.data.s3Url,
            fileName: textFileTos3.data.fileName,
            s3Key: textFileTos3.data.s3Key,
          };

          setTextFileDetails(fileInfo);
          return fileInfo;
        } else {
          console.error('Error uploading file to presigned URL:', uploadPresignedUrlResponse);
        }
      } else {
        console.error('Error getting signed URL:', textFileTos3Response);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  //generate Pdf
  const generatePdf = async () => {
    const missingFields = Object.keys(formData).filter((key) => {
      if (
        (key === 'material' || key === 'customMaterialClass') &&
        formData.materialClass != 'Liquid Epoxy' &&
        formData.materialClass != 'FBE'
      ) {
        return false;
      }
      return formData[key] === '';
    });

    if (missingFields.includes('material') && formData.material === '' && isOtherMaterialClass)
      missingFields.splice(missingFields.indexOf('material'), 1);

    if (missingFields.includes('customMaterialClass')) {
      missingFields.splice(missingFields.indexOf('customMaterialClass'), 1);
    }

    if (missingFields.includes('approvedBySign')) {
      missingFields.splice(missingFields.indexOf('approvedBySign'), 1);
    }
    if (missingFields.includes('approved2BySign')) {
      missingFields.splice(missingFields.indexOf('approved2BySign'), 1);
    }
    if (missingFields.includes('preparedBySign')) {
      missingFields.splice(missingFields.indexOf('preparedBySign'), 1);
    }
    if (missingFields.includes('generatorSettings')) {
      if (formData.required.generatorSettings === 'No') {
        missingFields.splice(missingFields.indexOf('generatorSettings'), 1);
      }
    }

    if (missingFields.includes('coilLength')) {
      if (formData.required.coilLength === 'No') {
        missingFields.splice(missingFields.indexOf('coilLength'), 1);
      }
    }

    if (missingFields.includes('cableLength')) {
      if (formData.required.cableLength === 'No') {
        missingFields.splice(missingFields.indexOf('cableLength'), 1);
      }
    }

    if (missingFields.includes('numberOfTurns')) {
      if (formData.required.numberOfTurns === 'No') {
        missingFields.splice(missingFields.indexOf('numberOfTurns'), 1);
      }
    }

    if (missingFields.length > 10) {
      const missingFieldNames = missingFields.map((field) => validation_fields[field]);
      console.log(missingFields, missingFieldNames);
      message.error(`Please fill in the following fields: ${missingFieldNames.join(', ')}`);
      return;
    } else if (missingFields.length > 0) {
      message.error(`Please fill the required fields`);
      console.log(missingFields);
      return;
    }

    if (formData.minTemp > formData.maxTemp) {
      message.error(`MinTemp is must be lesser or equal to ${formData.maxTemp}`);
      return;
    }

    if (formData.minTempS1 > formData.maxTempS1) {
      message.error(`MinTempS1 is must be lesser or equal to ${formData.maxTempS1}`);
      return;
    }

    setLoading(true);

    if (selectedFile) {
      const updatedFormData = new FormData();
      let heatprofiledata = {};
      // Helper function to append units to specific keys
      const appendUnit = (key, value) => `${value} ${formData.units[key]}`;

      // Iterate over formData entries
      Object.entries(formData).forEach(([key, value]) => {
        // Handle specific keys requiring unit appending
        const unitKeys = ['steelOD', 'coilLength', 'wt', 'cableLength', 'parentCoatingThickness'];
        if (unitKeys.includes(key)) {
          value = appendUnit(key, value);
        }

        if (key == 'signatureRequired' && value == false) {
          value = 'False';
        }

        heatprofiledata[key] = value;
        if (key == 'required') {
          value = JSON.stringify(value);
        }
        if (key == 'units') {
          value = JSON.stringify(value);
        }
        updatedFormData.append(key, value);
      });

      // Add missing fields that are required by the API
      updatedFormData.append('imageFileDetails', JSON.stringify(formData.imageFileDetails || {}));
      updatedFormData.append('textFileDetails', JSON.stringify(formData.textFileDetails || {}));
      updatedFormData.append('pdfReportDetails', JSON.stringify(formData.pdfReportDetails || {}));

      // Add ID if editing
      if (isEdit && listId) {
        updatedFormData.append('id', listId);
      }

      // Add timestamps
      updatedFormData.append('createdAt', formData.createdAt || moment().format('YYYY-MM-DD'));
      updatedFormData.append('updatedAt', moment().format('YYYY-MM-DD'));
      updatedFormData.append('totalCount', formData.totalCount || '0');

      // sanitising file name
      const sanitizedFileName = sanitizeFileName(selectedFile.name);
      const sanitizedFile = new File([selectedFile], sanitizedFileName, {
        type: selectedFile.type,
      });

      updatedFormData.append('file', sanitizedFile);
      updatedFormData.append('GeneratedDate', moment().utc().format('DD-MMM-YYYY HH:mm'));
      formData.units['GeneratedDate'] = moment().utc().format('DD-MMM-YYYY HH:mm');
      // console.log(heatprofiledata)

      try {
        const txtFileInfo = await uploadFileToS3(selectedFile);
        // const response = await axios.post('http://127.0.0.1:5000/generate-report', updatedFormData, {
        //     headers: {
        //         'Content-Type': 'multipart/form-data',
        //     },
        // });

        const response = await axios.post(
          process.env.REACT_APP_AWS_HEAT_PROFILE_REPORT_GENERATOR,
          updatedFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const reportUrlObject = response.data.report_url;

        setPDFReportDetails(reportUrlObject.Report_s3Url);
        setOutputFile(reportUrlObject.Report_s3Url.s3Url); // here -1
        isEdit
          ? await updateListData(reportUrlObject, txtFileInfo)
          : await handleSubmit(reportUrlObject, txtFileInfo);
        if (formData['signatureRequired'] && formData['signatureRequired'] == true) {
          setIsGenerated(true);
        }
        // message.success(`PDF generated successfully`);
      } catch (error) {
        console.error('Error:', error);
        message.error(`${selectedFile.name} file upload failed. 
                Please select a valid  file to upload`);
        //message.error('Please select a valid  file to upload.');
      }
    } else {
      message.error('Please select a valid file');
    }
    setLoading(false);
  };

  //final data save to DB
  const handleSubmit = async (reportUrlObject, txtFileInfo) => {
    setLoading(true);
    const payload = {
      heatProfile: {
        ...omit(formData, 'customMaterialClass'),
        pdfReportDetails: reportUrlObject.Report_s3Url,
        textFileDetails: txtFileInfo,
        imageFileDetails: reportUrlObject.Image_s3Url,
      },
    };
    //  console.log('data', heatProfile)

    try {
      const response = await axiosConfig.post('/technical-heat-profile', payload);
      message.success(`file uploaded successfully`);
      setShareButtonVisible(true);

      //getHeatProfileList();
      var obj = {
        pageSize: currentPagesize,
        pageNumber: currentPageNumber,
        searchKey: searchCriteria,
      };
      await onProjectList(obj);
      setEmailListId(response.data.data.id);
      await NotifyEmails(formData, isEdit);
    } catch (error) {
      console.error('Error submitting data:', error);
      message.error(`file upload failed.`);
    }
    setLoading(false);
  };

  //sent fileUrl to  emails
  const handleEmailSent = async (emails) => {
    // emails = emails.split(',');
    const payload = {
      emailDetails: {
        toAddresses: emails,
        heatProfileId: emailListId,
      },
    };

    try {
      const response = await axiosConfig.post('/technical-heat-profile/report', payload);
      message.success(`File will be sent to your e-mail shortly!`);
      handleEmailModalCancel();
      handleCancel();
    } catch (error) {
      console.error('Error submitting data:', error);
      message.error(`Please type valid EmailId!`);
    }
  };

  const setTemperatureBasedOnMaterial = (material) => {
    if (predefinedTemperatureRanges[material]) {
      setFormData((prevData) => ({
        ...prevData,
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
        stages: 1,
      }));
      form.setFieldsValue({
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
      });
    }
    if (
      (predefinedTemperatureRanges[material] && material == 'Canusa Liquid Epoxy Type P') ||
      material == 'Axalta Nap-Gard'
    ) {
      setFormData((prevData) => ({
        ...prevData,
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
        minTempS1: predefinedTemperatureRanges[material].minS1,
        maxTempS1: predefinedTemperatureRanges[material].maxS1,
        stages: '2',
      }));
      form.setFieldsValue({
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
        minTempS1: predefinedTemperatureRanges[material].minS1,
        maxTempS1: predefinedTemperatureRanges[material].maxS1,
        stages: '2',
      });
    }
  };

  const handleInputChange = (name, value) => {
    if (name === 'trailDate') {
      value = moment(value).format('YYYY-MM-DD');
    }

    if (name === 'materialClass') {
      setFormData((prevData) => ({ ...prevData, customMaterialClass: '' }));

      setFormData((prevData) => ({
        ...prevData,
        material: '',
        maxTemp: '0',
        minTemp: '0',
        minTempS1: '0',
        maxTempS1: '0',
        stages: '1',
      }));
      form.setFieldsValue({
        material: '',
        maxTemp: '',
        minTemp: '',
        minTempS1: '',
        maxTempS1: '',
        stages: 1,
      });
    }

    if (name === 'materialClass' && value === 'Others') {
      setFormData((prevData) => ({ ...prevData, material: '' }));
      form.setFieldsValue({
        minTemp: '',
        maxTemp: '',
        minTempS1: '',
        maxTempS1: '',
        stages: 1,
      });
    }

    if (name === 'material') {
      setTemperatureBasedOnMaterial(value);
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setIsInputfeildChanged(true);
  };

  const getvalue = (name) => {
    return formData[name];
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setSelectedFile(null);
    setOutputFile('');
    setSteelODUnit('mm');
    setWtUnits('mm');
    setCoilLengthUnit('mm');
    setcableUnit('meters');
    setParentCoatingThicknessUnit('mm');
    setIsInputfeildChanged(false);
    setIsGenerated(false);
    setSignatureData('');
    form.resetFields();
  };

  const handleCancel = async () => {
    setIsEdit(false);
    setIsOtherMaterialClass(false);
    setHeatProfilelistModalVisible(false);
    handleReset();
    setShareButtonVisible(false);
    setLoading(false);
    setSteelODUnit('mm');
    setWtUnits('mm');
    setCoilLengthUnit('meters');
    setcableUnit('mm');
    setParentCoatingThicknessUnit('mm');
    setIsInputfeildChanged(false);
    setIsGenerated(false);
    setSignatureData('');
  };

  const disabledDate = (current) => {
    return current && current > moment().endOf('day');
  };

  function hasNonEmptyTag(jsonObj) {
    for (const key in jsonObj) {
      if (jsonObj.hasOwnProperty(key)) {
        const value = jsonObj[key];

        if (value.trim() !== '') {
          return true;
        }
      }
    }
    return false;
  }

  useEffect(() => {
    const isvalid = hasNonEmptyTag(searchCriteria);
    let pageSize = currentPagesize;
    let pageNumber = currentPageNumber;

    if (hasMultiFacetSelection) {
      pageNumber = 1;
      const lc = Number(listCount);
      const total = Number.isFinite(lc) && lc > 0 ? lc : 500;
      pageSize = Math.min(HEAT_PROFILE_FACET_PAGE_CAP, Math.max(total, 500));
    } else if (isvalid) {
      pageSize = 10;
      pageNumber = 1;
    }

    onProjectList({
      pageNumber,
      pageSize,
      searchKey: searchCriteria,
    });
  }, [searchCriteria, hasMultiFacetSelection, currentPageNumber, currentPagesize, listCount]);

  // Build searchCriteria from all filter fields (text + dates + facet checkboxes); skip initial mount to avoid extra fetch
  const isFilterFirstRun = useRef(true);
  useEffect(() => {
    if (isFilterFirstRun.current) {
      isFilterFirstRun.current = false;
      return;
    }
    setCurrentPageNumber(1);
    const criteria = {};

    // Facet: single value only → API search. Multiple values → OR on client after a broad list fetch (comma string breaks the API).
    FILTERABLE_COLUMNS.forEach((col) => {
      const sel = columnSelectedValues[col];
      if (sel != null && sel.size === 1) {
        criteria[col] = Array.from(sel)[0];
      }
    });

    // Header / overlay text filters — skip columns that already use facet checkboxes (single or multi),
    // or single-facet already set in criteria; otherwise API search text can wipe the list (e.g. multi-select + Search box).
    Object.keys(columnFilters).forEach((key) => {
      if (criteria[key] != null && String(criteria[key]).length > 0) return;
      const facetSel = columnSelectedValues[key];
      if (facetSel != null && facetSel.size > 0) return;
      const val = (columnFilters[key] || '').trim();
      if (val) criteria[key] = val;
    });

    if (filterTrialDate) criteria.trailDate = moment(filterTrialDate).format('YYYY-MM-DD');
    if (filterCreatedDate) criteria.createdAt = moment(filterCreatedDate).format('YYYY-MM-DD');
    setSearchCriteria(criteria);
  }, [columnFilters, filterTrialDate, filterCreatedDate, columnSelectedValues]);

  const handleHeatProfileFilterChange = (value) => {
    // console.log(value);
  };

  const closeDrawer = () => {
    setSignatureData('');
    setOpenSignaturePad(false);
  };

  const handleCheckboxInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };
  const handleHeaderCheckboxChange = (type, isChecked, value) => {
    setIsInputfeildChanged(true);
    const category = 'required';
    setFormData((prevOptions) => {
      const updatedCategory = { ...prevOptions[category] };

      if (isChecked) {
        updatedCategory[type] = 'Yes';
      } else {
        updatedCategory[type] = 'No';
      }

      return {
        ...prevOptions,
        [category]: updatedCategory,
      };
    });
  };
  const UpdateRequiredCheck = (isChecked) => {
    if (isChecked) {
      handleHeaderCheckboxChange('generatorSettings', isChecked, 'Yes');
      handleHeaderCheckboxChange('coilLength', isChecked, 'Yes');
      handleHeaderCheckboxChange('cableLength', isChecked, 'Yes');
      handleHeaderCheckboxChange('numberOfTurns', isChecked, 'Yes');
    } else {
      handleHeaderCheckboxChange('generatorSettings', isChecked, 'No');
      handleHeaderCheckboxChange('coilLength', isChecked, 'No');
      handleHeaderCheckboxChange('cableLength', isChecked, 'No');
      handleHeaderCheckboxChange('numberOfTurns', isChecked, 'No');
    }
  };
  const isArrayNotEmpty = (type) => {
    const category = 'required';
    return (
      formData.hasOwnProperty(category) && // Check if the category exists in selectedOptions
      formData[category][type] != 'No' // Check if the array is not empty
    );
  };
  const isAnyPropertyWithValue = () => {
    return (
      isArrayNotEmpty('generatorSettings') ||
      isArrayNotEmpty('coilLength') ||
      isArrayNotEmpty('cableLength') ||
      isArrayNotEmpty('numberOfTurns')
    );
  };

  const handleUnitsChange = (type, value) => {
    setIsInputfeildChanged(true);
    const category = 'units';
    setFormData((prevOptions) => {
      const updatedCategory = { ...prevOptions[category] };
      updatedCategory[type] = value;

      return {
        ...prevOptions,
        [category]: updatedCategory,
      };
    });
  };

  const getCategoryValue1 = (type) => {
    const category = 'units';
    if (formData.hasOwnProperty(category)) {
      // Check if the category exists in selectedOptions
      return formData[category][type]; // Return the value for the category
    } else {
      return 'mm'; // Return null if the category does not exist
    }
  };

  const onValuesChange = (changedValues, allValues) => {
    const edited = { ...editedFields };

    Object.keys(changedValues).forEach((key) => {
      edited[key] = true;
    });
    // console.log(edited)
    setEditedFields(edited);
    setIsInputfeildChanged(true);
  };

  const AddNew = () => {
    return (
      <Modal
        width={960}
        className="pipeline-modal heatprofile-modal"
        title={isEdit ? 'Edit List Data' : 'Add New'}
        centered
        visible={heatProfilelistModalVisible}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}
        destroyOnClose
        footer={
          [
            <CrcButton
              key="submit1"
              label="Cancel"
              variant="tertiary"
              onClick={handleCancel}
              size="medium"
            />,
            <CrcButton
              key="submit2"
              label={loading ? (isEdit ? 'Updating…' : 'Creating…') : (isEdit ? 'Update' : 'Create Report')}
              variant={(isEdit && !isInputfeildChanged) || shareButtonVisible ? 'tertiary' : 'primary'}
              disabled={(isEdit && !isInputfeildChanged) || shareButtonVisible}
              onClick={() => generatePdf()}
              size="medium"
            />,
            isGenerated && formData['signatureRequired'] == 1 && (
              <Button
                size="medium"
                shape="round"
                type="primary"
                loading={ReviewAndSign}
                onClick={handleReviewAndSign}
              >
                Review & Sign
              </Button>
            ),

            shareButtonVisible && (
              <Button
                size="medium"
                shape="round"
                type="primary"
                onClick={handleAddEmailClick}
                style={{ zIndex: 1001 }}
              >
                Share Report
              </Button>
            ),
          ]
          //: [
          //     <Button key="submit3" type="primary" onClick={handleCancel}>
          //         Cancel
          //     </Button>,
          //     <Button
          //         key="submit4"
          //         size="medium"
          //         shape="round"
          //         type={shareButtonVisible ? 'default' : 'primary'}
          //         disabled={shareButtonVisible}
          //         loading={loading}
          //         onClick={generatePdf}
          //     >
          //         Create Report
          //     </Button>,
          //     shareButtonVisible && <Button key="submit4"
          //         size="medium"
          //         shape="round"
          //         type='primary'
          //         onClick={handleAddEmailClick}
          //         style={{ zIndex: 1001 }}
          //     >
          //         Share Report
          //     </Button>,
          // ]
        }
      >
        <Spin spinning={loading} tip="Wait: Report is being generated and uploaded...">
          <Form
            name="basic"
            layout="vertical"
            autoComplete="off"
            getcontainer={false}
            onValuesChange={onValuesChange}
            form={form}
          >
            <Row gutter={24} className="upload-row">
              <Col lg={6}>
                <Form.Item label="Report Number" name="reportNumber" required>
                  <Input
                    value={formData.reportNumber}
                    onChange={(e) => handleInputChange('reportNumber', e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item label="Project Name" name="projectName" required>
                  <Input
                    value={formData.projectName}
                    onChange={(e) => handleInputChange('projectName', e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item label="Project Number" name="projectNumber" required>
                  <Input
                    value={formData.projectNumber}
                    onChange={(e) => handleInputChange('projectNumber', e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item label="Report Author" name="reportAuthor" required>
                  <Input
                    value={formData.reportAuthor}
                    onChange={(e) => handleInputChange('reportAuthor', e.target.value)}
                    size="large"
                    disabled
                    defaultValue={formData.reportAuthor}
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item className="date-picker" label="Trial Date" name="trailDate" required>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={formData.trailDate ? moment(formData.trailDate) : null}
                    onChange={(date, dateString) => handleInputChange('trailDate', dateString)}
                    size="large"
                    format="YYYY-MM-DD"
                    disabledDate={disabledDate}
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item label="Generator ID" name="generatorId" required>
                  <Input
                    value={formData.generatorId}
                    onChange={(e) => handleInputChange('generatorId', e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item label="Induction Coil ID" name="inductionCoilId" required>
                  <Input
                    value={formData.inductionCoilId}
                    onChange={(e) => handleInputChange('inductionCoilId', e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>
              {/* <Col lg={6}>
                                <Row gutter={20}>
                                    <Col lg={14}>
                                        <Form.Item label={`Steel OD`} name="steelOD"
                                            required>
                                            <Input
                                                value={formData.steelOD}
                                                onChange={(e) => handleInputChange('steelOD', parseFloat(e.target.value))}
                                                size="large"
                                                type='number' />
                                        </Form.Item>
                                    </Col>
                                    <Col lg={10}>
                                        <Form.Item label='Unit' name="steelODUnit">
                                            <Select
                                                size="large"
                                                className='Unit-select'
                                                defaultValue={'mm'}
                                                value={steelODUnit}
                                                options={Units}
                                                onChange={(value) => setSteelODUnit(value)}

                                            >

                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col> */}

              <Col lg={8}>
                <Form.Item label={`Steel OD`} name="steelOD" required>
                  <div className="custom-input-group">
                    <Input
                      value={formData.steelOD}
                      onChange={(e) => handleInputChange('steelOD', parseFloat(e.target.value))}
                      size="large"
                      type="number"
                    />
                    <div className="report-radio">
                      <Radio.Group
                        style={{ display: 'flex' }}
                        value={getCategoryValue1('steelOD')}
                        onChange={(e) => {
                          handleUnitsChange('steelOD', e.target.value);
                          setSteelODUnit(e.target.value);
                        }}
                      >
                        <Radio value={'mm'}>mm</Radio>
                        <Radio value={'inch'}>inch</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item label={`Wt`} name="wt" required>
                  <div className="custom-input-group">
                    <Input
                      value={formData.wt}
                      onChange={(e) => handleInputChange('wt', e.target.value)}
                      size="large"
                      type="number"
                    />
                    <div className="report-radio">
                      <Radio.Group
                        style={{ display: 'flex' }}
                        value={getCategoryValue1('wt')}
                        onChange={(e) => {
                          setWtUnits(e.target.value);
                          handleUnitsChange('wt', e.target.value);
                        }}
                      >
                        <Radio value={'mm'}>mm</Radio>
                        <Radio value={'inch'}>inch</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>
              </Col>
              <Col lg={24}>
                <div style={{ marginBottom: '10px' }}>
                  <div className="heatp-bottom-border">&nbsp;</div>
                </div>
              </Col>
              {/* material class and material */}
              <Col lg={6} className="heat-profile-material-col">
                <Form.Item label="Material Class" name="materialClass" required>
                  <CustomSelect
                    options={materialClassOptions.filter(Boolean)}
                    placeholder="Select material class"
                    onChange={(value) => {
                      handleInputChange('materialClass', value);
                      setIsOtherMaterialClass(value === 'Others');
                    }}
                  />
                </Form.Item>
              </Col>
              {isOtherMaterialClass && (
                <Col lg={6}>
                  <Form.Item label="Other Material Class" name="customMaterialClass" required>
                    <Input
                      value={formData.materialClass}
                      onChange={(e) => handleInputChange('materialClass', e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              )}
              {!isOtherMaterialClass && (
                <Col lg={6} className="heat-profile-material-col">
                  <Form.Item label="Material" name="material" required>
                    <CustomSelect
                      options={materialOptions[formData.materialClass] ?? []}
                      placeholder="Select material"
                      disabled={!formData.materialClass}
                      onChange={(value) => handleInputChange('material', value)}
                    />
                  </Form.Item>
                </Col>
              )}
              {isOtherMaterialClass && (
                <Col lg={6}>
                  <Form.Item label="Stages" name="stages" required>
                    <Select
                      value={formData.stages}
                      onChange={(value) => handleInputChange('stages', value)}
                      size="large"
                      className="material-select"
                    >
                      <Option value={1}>Stage One</Option>
                      <Option value={2}>Stage Two</Option>
                    </Select>
                  </Form.Item>
                </Col>
              )}

              {formData.stages == 1 && !isOtherMaterialClass && (
                <>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                      <Input value={formData.minTemp} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Max Temp ${temperatureUnit}`} name="maxTemp" required>
                      <Input value={formData.maxTemp} size="large" disabled />
                    </Form.Item>
                  </Col>
                </>
              )}
              {formData.stages == 1 && isOtherMaterialClass && (
                <>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item
                      label={`Min Temp ${temperatureUnit}`}
                      name="minTemp"
                      rules={[
                        {
                          validator: (rule, value) => {
                            if (
                              formData.maxTemp !== undefined &&
                              parseFloat(formData.maxTemp) != 0 &&
                              parseFloat(value) > parseFloat(formData.maxTemp)
                            ) {
                              return Promise.reject('Min < MaxTemp');
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      required
                    >
                      <Input
                        value={formData.minTemp}
                        onChange={(e) => handleInputChange('minTemp', e.target.value)}
                        size="large"
                        type="number"
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Max Temp`} name="maxTemp" required>
                      <InputNumber
                        value={formData.maxTemp}
                        onChange={(value) => handleInputChange('maxTemp', value)}
                        size="large"
                        min={parseFloat(formData.minTemp)}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              {formData.stages == 2 && formData.material === 'Axalta Nap-Gard' && (
                <>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                      <Input value={formData.minTemp} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Max Temp ${temperatureUnit}`} name="maxTemp" required>
                      <Input value={formData.maxTemp} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Min Temp S1 `} name="minTempS1" required>
                      <Input value={formData.minTempS1} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Max Temp S1`} name="maxTempS1" required>
                      <Input value={formData.maxTempS1} size="large" disabled />
                    </Form.Item>
                  </Col>
                </>
              )}
              {formData.stages == 2 && formData.material == 'Canusa Liquid Epoxy Type P' && (
                <>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                      <Input value={formData.minTemp} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Max Temp ${temperatureUnit}`} name="maxTemp" required>
                      <Input value={formData.maxTemp} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Min Temp S1 `} name="minTempS1" required>
                      <Input value={formData.minTempS1} size="large" disabled />
                    </Form.Item>
                  </Col>
                  <Col lg={5} className="heat-profile-temp-col">
                    <Form.Item label={`Max Temp S1`} name="maxTempS1" required>
                      <Input value={formData.maxTempS1} size="large" disabled />
                    </Form.Item>
                  </Col>
                </>
              )}

              {formData.stages == '2' &&
                formData.material !== 'Canusa Liquid Epoxy Type P' &&
                formData.material !== 'Axalta Nap-Gard' && (
                  <>
                    <Col lg={5} className="heat-profile-temp-col">
                      <Form.Item
                        label={`Min Temp ${temperatureUnit}`}
                        name="minTemp"
                        required
                        rules={[
                          {
                            validator: (rule, value) => {
                              if (
                                formData.maxTemp !== undefined &&
                                parseFloat(formData.maxTemp) != 0 &&
                                parseFloat(value) > parseFloat(formData.maxTemp)
                              ) {
                                return Promise.reject('Min < MaxTemp');
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          value={formData.minTemp}
                          onChange={(e) => handleInputChange('minTemp', e.target.value)}
                          size="large"
                          type="number"
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={5} className="heat-profile-temp-col">
                      <Form.Item label={`Max Temp ${temperatureUnit}`} name="maxTemp" required>
                        <InputNumber
                          value={formData.maxTemp}
                          onChange={(value) => handleInputChange('maxTemp', value)}
                          size="large"
                          min={parseFloat(formData.minTemp)}
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={5} className="heat-profile-temp-col">
                      <Form.Item
                        label={`Min Temp S1 `}
                        name="minTempS1"
                        required
                        rules={[
                          {
                            validator: (rule, value) => {
                              if (
                                formData.maxTempS1 !== undefined &&
                                parseFloat(formData.maxTempS1) != 0 &&
                                parseFloat(value) > parseFloat(formData.maxTempS1)
                              ) {
                                return Promise.reject('Min < MaxTemp');
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          value={formData.minTempS1}
                          onChange={(e) => handleInputChange('minTempS1', e.target.value)}
                          size="large"
                          type="number"
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={5} className="heat-profile-temp-col">
                      <Form.Item label={`Max Temp S1 `} name="maxTempS1" required>
                        <InputNumber
                          value={formData.maxTempS1}
                          onChange={(value) => handleInputChange('maxTempS1', value)}
                          size="large"
                          // type="number"
                          min={parseFloat(formData.minTempS1)}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}

              <Col lg={24} className="heat-profile-section-divider-col">
                <div className="heat-profile-section-divider">
                  <div className="heatp-bottom-border">&nbsp;</div>
                </div>
              </Col>
              <Col lg={24} className="heat-profile-required-label-col">
                <Checkbox
                  label="Required to Report"
                  onChange={(checked) => {
                    UpdateRequiredCheck(checked);
                  }}
                  checked={isAnyPropertyWithValue()}
                  borderColor="#00C9A7"
                />
              </Col>

              <Col lg={8} className="heat-profile-report-field-col">
                <div className="heat-profile-checkbox-label">
                  <Checkbox
                    label={
                      <span>
                        Generator Settings{' '}
                        {isArrayNotEmpty('generatorSettings') && <a style={{ color: 'red' }}>*</a>}
                      </span>
                    }
                    onChange={(checked) => {
                      handleHeaderCheckboxChange('generatorSettings', checked);
                    }}
                    checked={isArrayNotEmpty('generatorSettings')}
                    borderColor="#00C9A7"
                  />
                </div>
                <Form.Item name="generatorSettings">
                  <Input
                    disabled={!isArrayNotEmpty('generatorSettings')}
                    value={formData.generatorSettings}
                    onChange={(e) => handleInputChange('generatorSettings', e.target.value)}
                    size="large"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
              <Col lg={8} className="heat-profile-report-field-col">
                <div className="heat-profile-checkbox-label">
                  <Checkbox
                    label={
                      <span>
                        Coil Length{isArrayNotEmpty('coilLength') && <a style={{ color: 'red' }}>*</a>}
                      </span>
                    }
                    onChange={(checked) => {
                      handleHeaderCheckboxChange('coilLength', checked);
                    }}
                    checked={isArrayNotEmpty('coilLength')}
                    borderColor="#00C9A7"
                  />
                </div>
                <Form.Item name="coilLength">
                  <div className="custom-input-group">
                    <Input
                      disabled={!isArrayNotEmpty('coilLength')}
                      value={formData.coilLength}
                      onChange={(e) => handleInputChange('coilLength', e.target.value)}
                      size="large"
                      type="number"
                    />
                    <div className="report-radio">
                      <Radio.Group
                        style={{ display: 'flex' }}
                        disabled={!isArrayNotEmpty('coilLength')}
                        value={getCategoryValue1('coilLength')}
                        onChange={(e) => {
                          setCoilLengthUnit(e.target.value);
                          handleUnitsChange('coilLength', e.target.value);
                        }}
                      >
                        <Radio value={'mm'}>mm</Radio>
                        <Radio value={'inch'}>inch</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>
              </Col>

              <Col lg={8} className="heat-profile-report-field-col">
                <div className="heat-profile-checkbox-label">
                  <Checkbox
                    label={
                      <span>
                        Cable Length{' '}
                        {isArrayNotEmpty('cableLength') && <a style={{ color: 'red' }}>*</a>}
                      </span>
                    }
                    onChange={(checked) => {
                      handleHeaderCheckboxChange('cableLength', checked);
                    }}
                    checked={isArrayNotEmpty('cableLength')}
                    borderColor="#00C9A7"
                  />
                </div>
                <Form.Item name="cableLength">
                  <div className="custom-input-group">
                    <Input
                      disabled={!isArrayNotEmpty('cableLength')}
                      value={formData.cableLength}
                      onChange={(e) => handleInputChange('cableLength', e.target.value)}
                      size="large"
                      type="number"
                    />
                    <div className="report-radio">
                      <Radio.Group
                        style={{ display: 'flex' }}
                        disabled={!isArrayNotEmpty('cableLength')}
                        value={getCategoryValue1('cableLength')}
                        onChange={(e) => {
                          setcableUnit(e.target.value);
                          handleUnitsChange('cableLength', e.target.value);
                        }}
                      >
                        <Radio value={'meters'}>meters</Radio>
                        <Radio value={'feet'}>feet</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>
              </Col>
              <Col lg={6} className="heat-profile-report-field-col">
                <div className="heat-profile-checkbox-label">
                  <Checkbox
                    label={
                      <span>
                        Number of Turns{' '}
                        {isArrayNotEmpty('numberOfTurns') && <a style={{ color: 'red' }}>*</a>}
                      </span>
                    }
                    onChange={(checked) => {
                      handleHeaderCheckboxChange('numberOfTurns', checked);
                    }}
                    checked={isArrayNotEmpty('numberOfTurns')}
                    borderColor="#00C9A7"
                  />
                </div>
                <Form.Item name="numberOfTurns">
                  <Input
                    disabled={!isArrayNotEmpty('numberOfTurns')}
                    value={formData.numberOfTurns}
                    onChange={(e) => handleInputChange('numberOfTurns', e.target.value)}
                    size="large"
                    type="number"
                  />
                </Form.Item>
              </Col>

              <Col lg={6}>
                <Form.Item label={`Cut back length`} name="cutbackLength" required>
                  <Input
                    value={formData.cutbackLength}
                    onChange={(e) => handleInputChange('cutbackLength', e.target.value)}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                {/* <Row gutter={20} style={{ paddingBottom: '25px' }}>
                                    <Col lg={15}>
                                        <Form.Item label={`Parent Coating Thickness`} name="parentCoatingThickness"
                                            required>
                                            <Input
                                                value={formData.parentCoatingThickness}
                                                onChange={(e) => handleInputChange('parentCoatingThickness', e.target.value)}
                                                size="large"
                                                type='number' />
                                        </Form.Item>
                                    </Col>
                                    <Col lg={9}>
                                        <Form.Item label='Unit' name="parentCoatingThicknessthUnits" style={{ paddingTop: '20px' }}
                                        >
                                            <Select
                                                size="large"
                                                className='Unit-select'
                                                defaultValue={'mm'}
                                                value={parentCoatingThicknessUnit}
                                                options={Units}
                                                onChange={(value) => setParentCoatingThicknessUnit(value)}

                                            >

                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row> */}
                <Form.Item
                  label={`Parent Coating Thickness`}
                  name="parentCoatingThickness"
                  required
                >
                  <div className="custom-input-group">
                    <Input
                      value={formData.parentCoatingThickness}
                      onChange={(e) => handleInputChange('parentCoatingThickness', e.target.value)}
                      size="large"
                      type="number"
                    />
                    <div className="report-radio">
                      <Radio.Group
                        style={{ display: 'flex' }}
                        value={getCategoryValue1('parentCoatingThickness')}
                        onChange={(e) => {
                          setParentCoatingThicknessUnit(e.target.value);
                          handleUnitsChange('parentCoatingThickness', e.target.value);
                        }}
                      >
                        <Radio value={'µm'}>µm</Radio>
                        <Radio value={'mm'}>mm</Radio>
                        <Radio value={'inch'}>inch</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>
              </Col>
              <Col lg={6} className="heat-profile-probe-signature-row">
                <Form.Item label={`Probe Temp (${temperatureUnit})`} name="probeTemp" required>
                  <Input
                    value={formData.probeTemp}
                    onChange={(e) => handleInputChange('probeTemp', e.target.value)}
                    size="large"
                    type="number"
                  />
                </Form.Item>
              </Col>
              <Col lg={6} className="heat-profile-probe-signature-row">
                <Form.Item label="Probe Temp Location" name="probeTempLocation" required>
                  <Input
                    value={formData.probeTempLocation}
                    onChange={(e) => handleInputChange('probeTempLocation', e.target.value)}
                    size="large"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>

              <Col lg={6} className="heat-profile-probe-signature-row">
                <Form.Item label="Signature Required" name="signatureRequired" required>
                  {/* <Select
                                        value={formData.signatureRequired}
                                        onChange={(value) => handleInputChange('signatureRequired', value)}
                                        size="large"
                                        className='material-select'

                                    >
                                        <Option value={true}>Yes</Option>
                                        <Option value={false}>No!</Option>
                                    </Select> */}

                  <div
                    className="report-radio"
                    style={{ flexDirection: 'column', paddingTop: '00px' }}
                  >
                    <Radio.Group
                      style={{ display: 'flex' }}
                      value={getvalue('signatureRequired')}
                      onChange={(e) => {
                        handleInputChange('signatureRequired', e.target.value);
                      }}
                    >
                      <Radio value={0}>No</Radio>
                      <Radio value={1}>Yes</Radio>
                    </Radio.Group>
                  </div>
                </Form.Item>
              </Col>

              <Col lg={6} className="heat-profile-probe-signature-row">
                <Form.Item label="Select file" required className="uploadFile_heatProfile">
                  <Upload
                    maxCount={1}
                    showUploadList={false}
                    onChange={handleChange}
                    accept=".txt,.csv"
                    fileList={fileList}
                  >
                    <Button
                      className="selectTxtFile"
                      icon={<CloudUploadOutlined className="selectTxtFile" />}
                    >
                      Select File
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col lg={8}>
                {selectedFile && (
                  <Form.Item label="Selected file" required>
                    {selectedFile && <p className="selectTxtFile">{selectedFile.name}</p>}
                  </Form.Item>
                )}
              </Col>
              <Col lg={6}>
                {outputFile ? (
                  <Form.Item label="Generated PDF" required>
                    <a
                      href={outputFile}
                      style={{
                        borderRadius: '50%',
                        backgroundColor: 'red',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '30px',
                        height: '30px',
                        cursor: 'pointer',
                      }}
                    >
                      <DownloadOutlined style={{ color: 'white' }} />
                    </a>
                  </Form.Item>
                ) : null}
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    );
  };

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-auto">
        {
          <div className="flex-auto heat-profile-card">
            <div className="heat-profile-list-header">
              <div className="heat-profile-header-left">
                <div className="heat-profile-icon-wrap">
                  <HeatMapOutlined style={{ fontSize: 22 }} />
                </div>
                <div className="heat-profile-header-content">
                  <div className="heat-profile-title-row">
                    <h3 className="heat-profile-list-title">List of Heat Profiles</h3>
                    <span className="heat-profile-summary">
                      {hasMultiFacetSelection
                        ? displayedProjectList.length
                        : listCount != null
                          ? listCount
                          : 0}{' '}
                      item
                      {(hasMultiFacetSelection
                        ? displayedProjectList.length
                        : listCount != null
                          ? listCount
                          : 0) !== 1
                        ? 's'
                        : ''}
                    </span>
                  </div>
                  <p className="heat-profile-list-description">
                    Manage and generate heat profile reports for coating technical operations.
                  </p>
                </div>
              </div>
              <div className="heat-profile-header-actions calibration-modern-actions">
                <CrcButton
                  label="Create Heat Profile"
                  variant="primary"
                  icon={Plus}
                  onClick={() => showAddEditListModal('', false)}
                  size="small"
                />
              </div>
            </div>
            <div className="addTeamDetails heat-profile-table-wrap">
              <Table
                columns={columns}
                dataSource={heatProfileTableRows}
                className="h-100 pipeline-table equipPaginationTable heat-profile-table"
                scroll={{ x: 1720 }}
                onChange={(pagination, filters, sorter) => {
                  if (sorter && sorter.columnKey) {
                    setSortField(sorter.columnKey);
                    setSortOrder(sorter.order || null);
                    setCurrentPageNumber(1);
                  }
                }}
                pagination={
                  hasMultiFacetSelection
                    ? displayedProjectList.length > 25
                      ? {
                          position: ['bottomRight'],
                          showSizeChanger: true,
                          className: 'heat-profile-pagination',
                          total: displayedProjectList.length,
                          current: currentPageNumber,
                          pageSize: currentPagesize,
                          pageSizeOptions: ['10', '25', '50', '100'],
                          onChange: (pageNumber, pageSize) => {
                            setCurrentPageNumber(pageNumber);
                            setCurrentPagesize(pageSize);
                          },
                        }
                      : false
                    : listCount > 10
                      ? {
                          position: ['bottomRight'],
                          showSizeChanger: true,
                          className: 'heat-profile-pagination',
                          total: listCount,
                          current: currentPageNumber,
                          pageSize: currentPagesize,
                          pageSizeOptions: ['10', '20', '50', '100'],
                          onChange: async (pageNumber, pageSize) => {
                            setCurrentPageNumber(pageNumber);
                            setCurrentPagesize(pageSize);
                            var obj = {
                              pageSize: pageSize,
                              pageNumber: pageNumber,
                              searchKey: searchCriteria,
                            };
                            await onProjectList(obj);
                          },
                        }
                      : false
                }
                loading={{
                  indicator: <Spinner variant="inline" size="medium" />,
                  spinning: isLoading,
                }}
              />
            </div>
          </div>
        }

        {AddNew()}

        <Modal
          width={480}
          className="pipeline-modal heatprofile-modal"
          title={
            <span style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Delete Heat Profile
            </span>
          }
          style={{ top: 100 }}
          visible={isDeleteModalVisible}
          onCancel={handleDeleteCancel}
          footer={[
            <CrcButton
              key="cancel"
              label="Cancel"
              variant="secondary"
              onClick={handleDeleteCancel}
            />,
            <CrcButton
              key="delete"
              label={isDeleteLoading ? 'Deleting...' : 'Delete'}
              variant="primary"
              onClick={handleDeleteOk}
              loading={isDeleteLoading}
              style={{ backgroundColor: '#EF314C', borderColor: '#EF314C', marginLeft: '8px' }}
            />,
          ]}
        >
          <div style={{ padding: '0px', fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>
            <p style={{ marginBottom: '16px' }}>
              Deleting Heat Profile Details will delete all the corresponding and related
              information.
            </p>
            <p style={{ margin: 0 }}>
              Are you sure you want to delete report{' '}
              <b style={{ color: '#EF314C', fontWeight: 600 }}>
                {selectedData && selectedData.reportNumber}
              </b>
              ?
            </p>
          </div>
        </Modal>

        {emailModalVisible && (
          <EmailModal
            visible={emailModalVisible}
            onCancel={handleEmailModalCancel}
            onSubmit={handleEmailSent}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ heatProfileReducer }) => {
  const projectList = get(heatProfileReducer, 'heatProfileList', []);

  const isLoading = get(heatProfileReducer, 'isHeatProfListLoading', false);
  const listCount = get(heatProfileReducer, 'ListCount', null);

  return {
    projectList,
    isLoading,
    listCount,
  };
};

const mapDispatchToProps = {
  onProjectList: fetchHeatProfileList,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeatProfile);
